/**
 * DemoShareRedeem — Iter275.8
 *
 * Lands here from a `/d/{token}` share link (e.g. emailed to a prospect
 * by the operator who built the demo session). On mount we POST the
 * token to `/api/demo-share/redeem`. On success the backend sets the
 * `qt_user_token` cookie and we redirect to /account/subscriptions.
 * On failure we render a friendly explainer keyed off the HTTP status.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import {
  Sparkles, Loader2, AlertOctagon, CheckCircle2, Mail, ArrowRight,
} from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import useDocumentTitle from "../hooks/useDocumentTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status → human copy. Backend slugs map 1-to-1.
const ERROR_COPY = {
  400: {
    title: "Invalid link",
    body:  "This demo share link is malformed or missing its token. Ask the operator who sent it to share a fresh one.",
  },
  403: {
    title: "Session expired",
    body:  "This demo session is no longer active. The operator who set it up can extend the window or send a new link.",
  },
  404: {
    title: "Link not recognised",
    body:  "We couldn't find a demo session that matches this link. It may have been rotated or revoked.",
  },
  409: {
    title: "Link already used",
    body:  "This share link is single-use and has already been redeemed once. Contact the operator for a fresh link.",
  },
};


export default function DemoShareRedeem() {
  useDocumentTitle("Redeeming your demo session — QorTrace");
  const { token } = useParams();
  const navigate = useNavigate();
  // Guard against React-StrictMode double-mount calling the redeem
  // endpoint twice (which would burn the single-use token).
  const triedRef = useRef(false);
  const [state, setState] = useState("redeeming"); // redeeming | ok | err
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (triedRef.current) return;
    triedRef.current = true;
    (async () => {
      try {
        const r = await axios.post(
          `${API}/demo-share/redeem`,
          { token: token || "" },
          { withCredentials: true },
        );
        setSuccess(r.data);
        setState("ok");
        // Smooth pause so the operator's prospect actually sees the
        // confirmation card before we bounce them to the dashboard.
        setTimeout(() => {
          navigate(r.data?.redirect_to || "/account/subscriptions",
                    { replace: true });
        }, 1400);
      } catch (e) {
        const status = e?.response?.status || 500;
        const copy = ERROR_COPY[status] || {
          title: "Something went wrong",
          body:  e?.response?.data?.detail || "Please ask the operator to send a fresh share link.",
        };
        setError({ status, ...copy });
        setState("err");
      }
    })();
  }, [token, navigate]);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--qt-bg)" }}>
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="qt-card w-full max-w-[560px]"
             data-testid="demo-redeem-card"
             style={{
               border: state === "err" ? "1px solid #FF6E6E"
                                        : "1px solid var(--qt-cyan)",
               background: state === "err"
                 ? "linear-gradient(135deg, rgba(255,110,110,0.06), transparent 70%)"
                 : "linear-gradient(135deg, rgba(0,229,255,0.06), transparent 70%)",
               padding: "32px 36px",
             }}>
          {state === "redeeming" && (
            <>
              <div className="flex items-center gap-3">
                <Sparkles size={22} style={{ color: "var(--qt-cyan)" }} />
                <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>
                  DEMO SESSION · UNLOCKING
                </div>
              </div>
              <h1 className="qt-h2 text-2xl mt-3" style={{ color: "#fff" }}>
                One moment — signing you in.
              </h1>
              <p className="text-sm mt-2" style={{ color: "var(--qt-text-2)" }}>
                Validating your share link and provisioning your demo
                entitlements. You'll land in the dashboard in a second.
              </p>
              <div className="mt-6 flex items-center gap-2 qt-mono text-xs"
                   data-testid="demo-redeem-spinner"
                   style={{ color: "var(--qt-text-3)", letterSpacing: "0.14em" }}>
                <Loader2 size={14} className="animate-spin" />
                CLAIMING TOKEN · PINNING TO YOUR IP · ISSUING SESSION
              </div>
            </>
          )}

          {state === "ok" && (
            <>
              <div className="flex items-center gap-3">
                <CheckCircle2 size={22} style={{ color: "#1FDD83" }} />
                <div className="qt-label" style={{ color: "#1FDD83" }}>
                  DEMO SESSION · UNLOCKED
                </div>
              </div>
              <h1 className="qt-h2 text-2xl mt-3" style={{ color: "#fff" }}>
                Welcome{success?.account?.name ? `, ${success.account.name.split(" ")[0]}` : ""}.
              </h1>
              <p className="text-sm mt-2" style={{ color: "var(--qt-text-2)" }}>
                You're signed in as <b style={{ color: "var(--qt-text-1)" }}>
                {success?.account?.email}</b> with the demo entitlements
                pre-unlocked. Redirecting to your dashboard…
              </p>
              {success?.beta_admin_access && (
                <div className="mt-4 qt-mono text-[11px]"
                     data-testid="demo-redeem-beta-admin-chip"
                     style={{
                       color: "#A777FF",
                       background: "rgba(167,119,255,0.10)",
                       border: "1px solid rgba(167,119,255,0.45)",
                       padding: "6px 10px", borderRadius: 4,
                       letterSpacing: "0.14em", fontWeight: 700,
                       display: "inline-block",
                     }}>
                  BETA-ADMIN BROWSE ACCESS GRANTED
                </div>
              )}
              <div className="mt-6 qt-mono text-xs"
                   style={{ color: "var(--qt-text-3)", letterSpacing: "0.14em" }}>
                REDIRECTING…
              </div>
            </>
          )}

          {state === "err" && (
            <>
              <div className="flex items-center gap-3">
                <AlertOctagon size={22} style={{ color: "#FF6E6E" }} />
                <div className="qt-label" style={{ color: "#FF6E6E" }}>
                  DEMO LINK · {error.status || ""}
                </div>
              </div>
              <h1 className="qt-h2 text-2xl mt-3" style={{ color: "#fff" }}
                  data-testid="demo-redeem-error-title">
                {error?.title}
              </h1>
              <p className="text-sm mt-2"
                 data-testid="demo-redeem-error-body"
                 style={{ color: "var(--qt-text-2)" }}>
                {error?.body}
              </p>
              <div className="mt-6 flex items-center gap-2 flex-wrap">
                <a href="mailto:hello@qortrace.com"
                   className="qt-btn qt-btn-primary inline-flex"
                   data-testid="demo-redeem-contact-btn"
                   style={{ fontSize: 13, padding: "0.55rem 0.9rem" }}>
                  <Mail size={13} /> Contact your operator
                </a>
                <a href="/"
                   className="qt-btn qt-btn-ghost inline-flex"
                   style={{ fontSize: 13, padding: "0.55rem 0.9rem",
                             color: "var(--qt-text-2)" }}>
                  Visit qortrace.com <ArrowRight size={13} />
                </a>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
